<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { DxList } from 'devextreme-vue/list'

import { useODataSource } from '@/composables/data-grid'
import { entityChangeTypeEnum } from '@/stores/portal.store'
import { useEntityChanges } from '@/composables/entity-changes'
import { useApps, useDetectDevice } from '@/composables/device'
import { useHighlightNewRow } from '@/composables/scroll'

import { planningApi } from '@/apis/planning.api'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import FilterSortSelector from '@/components/shared/FilterSortSelector.vue'
import StaffDetail from '@/components/shared/StaffDetail.vue'
import StaffEdit from '@/components/shared/StaffEdit.vue'
import StaffListItem from '@/components/shared/StaffListItem.vue'
import SignalBar from '@/components/shared/signal-bar/SignalBar.vue'

const route = useRoute()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()
const { phoneCall, whatsappMessage } = useApps()
const { isMobile } = useDetectDevice()
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()

const listContainer = ref(null)
let listInstance = null

onMounted(async() => {
  if (planningStore.groups.length === 0) await planningStore.fetchGroupsAsync({ all: true }, true)
})

const onCallClick = (item) => phoneCall(item.phone)
const onWhatsAppClick = (item) => whatsappMessage(item.phone)
const isEmptyDataSource = ref(false)
const selectedItem = ref(null)
const editItem = ref(null)


const quickCreate = ref(false)
const onAddClick = (quick = false) => { quickCreate.value = quick === true; editItem.value = {} }

webSiteStore.fabActions = webSiteStore.can(webSiteStore.permissionFlagEnum.MASTER_DATA) ? [
  {
    id: 'add',
    icon: 'mdi-plus',
    onClick: () => onAddClick(false),
  },
  {
    id: 'quick-create',
    icon: 'mdi-flash',
    onClick: () => onAddClick(true),
  },
] : []

const dataSource = useODataSource(planningApi.getAbsoluteUrl() + planningApi.staffsUrl, {
  onChanged: () => {
    isEmptyDataSource.value = dataSource.totalCount() === 0
  },
  searchExpr: 'search_field',
})

const getRandomNumber = () => (Math.floor(Math.random() * 100) % 5) + 1

const onDetailUpdate = () => {
  selectedItem.value = null
  dataSource.pageIndex(0)
  dataSource.reload()
}

const onEditItemCreated = async () => {
  editItem.value = null
  webSiteStore.activateUpdatedDateSort(route.name)
  dataSource.sort(webSiteStore.buildSortOrderBy(route.name))
  dataSource.pageIndex(0)
  const loaded = await dataSource.reload()
  await highlightRowAsync(loaded?.[0]?.id, listContainer)
  listInstance?.repaint()
}

const onEditDismissed = () => {
  editItem.value = null
  dataSource.reload()
}


useEntityChanges([entityChangeTypeEnum.STAFF], (change) => (change.created ? onEditItemCreated() : dataSource.reload()), () => editItem.value !== null)
</script>
<template>
  <div>
    <filter-sort-selector
      :data-source="dataSource"
      :kpi-enabled="true"
      :kpi-visible="webSiteStore.isKpiBarVisible('staffs')"
      @toggle-kpi="webSiteStore.toggleKpiBar('staffs')"
    />
    <signal-bar page="staffs" embedded :visible="webSiteStore.isKpiBarVisible('staffs')" />
    <q-card flat>
      <q-card-section ref="listContainer" class="q-pa-none">
        <q-dialog
          maximized
          :model-value="selectedItem !== null"
        >
          <staff-detail
            :model-value="selectedItem"
            @cancel="onDetailUpdate"
            @update="onDetailUpdate"
          />
        </q-dialog>
        <q-dialog
          :maximized="isMobile"
          :model-value="editItem !== null"
          class="dialog-sm"
        >
          <staff-edit
            v-if="editItem !== null"
            :model-value="editItem"
            :initial-quick-create="quickCreate"
            @update="onEditItemCreated"
            @cancel="onEditDismissed"
            @delete="onEditDismissed"
          />
        </q-dialog>
        <dx-list
          v-show="!isEmptyDataSource"
          :data-source="dataSource"
          height="calc(100vh - 8rem)"
          page-load-mode="scrollBottom"
          class="events-list"
          item-template="item"
          selection-mode="none"
          :active-state-enabled="false"
          @initialized="e => listInstance = e.component"
        >
          <template #item="{ data: staff }">
            <q-item>
              <staff-list-item
                :key="staff.id"
                :staff="staff"
                class="q-pa-sm"
                :class="{ 'row-highlight': staff.id === highlightedRowId }"
                dense
                clickable
                @click="selectedItem = staff"
              >
                <template #right>
                  <q-btn
                    round
                    color="positive"
                    size="1rem"
                    style="margin: 0;"
                    flat
                    icon="mdi-whatsapp"
                    @click.stop="onWhatsAppClick(staff)"
                  />
                  <q-btn
                    round
                    color="blue"
                    size="1rem"
                    style="margin: 0; margin-left: .5rem;"
                    flat
                    icon="mdi-phone"
                    @click.stop="onCallClick(staff)"
                  />
                </template>
              </staff-list-item>
            </q-item>
          </template>
        </dx-list>
        <div
          v-show="isEmptyDataSource"
          class="empty-container"
        >
          <img
            src="/img/no-staffs.svg"
            :style="{ maxWidth: '80vw', objectFit: 'contain' }"
            class="q-mb-xl"
          >
          <p class="text-h5">
            {{ $t('common.noStaff') }}
          </p>
          <p class="sub text-weight-light">
            {{ $t(`common.noStaff${getRandomNumber()}`) }}
          </p>
        </div>
      </q-card-section>
    </q-card>
  </div>
</template>
